/**
 * Playbooks QA round 4, U3 (S3) — a pure, DOM-free describer that turns a
 * segmentation's stored `SegmentationConditions` into plain-text lines for a
 * read-only accordion body (the "Pick an existing segmentation to add to
 * this ladder" picker, `QualifyStep.tsx`'s `AddSegmentationDrawerBody`).
 *
 * Deliberately NOT a `FilterPopover` read-only mode (no such prop exists,
 * and adding one would touch core Flow code) and NOT interactive chips
 * (Spencer, 2026-09-13 decision 5: a text summary). Driven by the same
 * `CAMPAIGN_MEMBER_FILTER_FIELDS` catalog and `FILTER_OPERATORS_BY_TYPE`
 * operator vocabulary the real editor uses, so an unmapped field/operator
 * combination here reflects a real drift, not a second guess at the truth.
 *
 * Field/operator lookups degrade gracefully rather than throwing: an unknown
 * field key falls back to the raw key, an unknown operator falls back to the
 * raw operator string. A segmentation's stored conditions can reference a
 * field/operator that has since been removed from the catalogs — this is a
 * read-only summary, not a validator, so it always renders SOMETHING.
 */
import type { FilterCondition, FilterFieldType, FilterGroup } from '../_shared/flow/filter';
import type { FilterFieldCatalogEntry, FilterOperatorOption } from '../_shared/flow/filter';
import type { SegmentationConditions } from './schemas';

const NO_CONDITIONS_LINE = 'No conditions — matches every member';
// Codex round-4 blocking #4 — `null` means "no version yet" OR "drifted/
// unparseable JSON" (`getLibrary`'s `parseDbJson` degrades to null rather
// than throwing), neither of which is a known fact about the audience. Only
// a VALID `{ groups: [] }` (or all-empty groups, see the empty-group filter
// below) may claim to match every member; `null` must say it doesn't know.
const CONDITIONS_UNAVAILABLE_LINE = 'Conditions unavailable';

// Universal no-value operators (mirrors `condition-to-sql.ts`'s
// "is_empty"/"is_not_empty" short-circuit, which runs before any per-type
// switch): rendering a value after these would be nonsensical.
const NO_VALUE_OPERATORS = new Set(['is_empty', 'is_not_empty']);

function fieldEntry(
  fields: readonly FilterFieldCatalogEntry[],
  key: string,
): FilterFieldCatalogEntry | undefined {
  return fields.find((f) => f.key === key);
}

function operatorLabel(
  operatorsByType: Record<FilterFieldType, readonly FilterOperatorOption[]>,
  fieldType: FilterFieldType | undefined,
  operatorValue: string,
): string {
  if (fieldType) {
    // Codex round-4 non-blocking #2 — the field's type IS known, so its own
    // catalog is authoritative. A miss here means the stored operator is not
    // valid for this field type (stale/drifted data, e.g. `not_equals` on a
    // `boolean` field, which `FILTER_OPERATORS_BY_TYPE.boolean` deliberately
    // excludes) — searching every OTHER type's catalog would silently borrow
    // a label (e.g. text's "is not") that hides exactly that drift. Return
    // the raw operator string instead.
    const found = operatorsByType[fieldType]?.find((o) => o.value === operatorValue);
    return found ? found.label : operatorValue;
  }
  // Field type unknown (unknown field key, so there's no catalog to check
  // against at all) — fall back to a search across every type before giving
  // up and showing the raw operator string.
  for (const list of Object.values(operatorsByType)) {
    const found = list.find((o) => o.value === operatorValue);
    if (found) return found.label;
  }
  return operatorValue;
}

function formatValue(value: FilterCondition['value'], fieldType: FilterFieldType | undefined): string {
  if (Array.isArray(value)) return value.join(', ');
  if (fieldType === 'boolean') {
    if (value === 'true') return 'yes';
    if (value === 'false') return 'no';
  }
  if (value === null || value === undefined) return '';
  return String(value);
}

function formatCondition(
  condition: FilterCondition,
  fields: readonly FilterFieldCatalogEntry[],
  operatorsByType: Record<FilterFieldType, readonly FilterOperatorOption[]>,
): string {
  const entry = fieldEntry(fields, condition.field);
  const label = entry?.label ?? condition.field;
  const opLabel = operatorLabel(operatorsByType, entry?.type, condition.operator);
  if (NO_VALUE_OPERATORS.has(condition.operator)) {
    return `${label} ${opLabel}`;
  }
  const valueText = formatValue(condition.value, entry?.type);
  return valueText === '' ? `${label} ${opLabel}` : `${label} ${opLabel} ${valueText}`;
}

function describeGroup(
  group: FilterGroup,
  fields: readonly FilterFieldCatalogEntry[],
  operatorsByType: Record<FilterFieldType, readonly FilterOperatorOption[]>,
): string {
  return group.conditions
    .map((condition) => formatCondition(condition, fields, operatorsByType))
    .join(` ${group.logic.toUpperCase()} `);
}

/**
 * `conditions === null` — a head-less identity (no version yet) or a
 * drifted/unparseable JSON column (`getLibrary`'s `parseDbJson` degrades to
 * null rather than throwing) — is UNKNOWN, not "matches every member": a
 * rep picking a segmentation must not be told it matches everyone when the
 * truth is "we don't know what this matches." Codex round-4 blocking #4.
 *
 * Only a valid `{ groups: [] }` — or every group present having no
 * conditions of its own (Codex round-4 non-blocking #1: a schema-valid
 * `{ groups: [{ logic: 'and', conditions: [] }] }` is a REAL, known "matches
 * everyone", not a drift signal) — earns the matches-every-member line.
 * Empty groups are dropped before joining so they never render a blank line.
 */
export function describeConditions(
  conditions: SegmentationConditions | null,
  fields: readonly FilterFieldCatalogEntry[],
  operatorsByType: Record<FilterFieldType, readonly FilterOperatorOption[]>,
): string[] {
  if (!conditions) {
    return [CONDITIONS_UNAVAILABLE_LINE];
  }

  const nonEmptyGroups = conditions.groups.filter((group) => group.conditions.length > 0);
  if (nonEmptyGroups.length === 0) {
    return [NO_CONDITIONS_LINE];
  }

  const lines: string[] = [];
  nonEmptyGroups.forEach((group, index) => {
    if (index > 0) {
      lines.push(`— ${conditions.groupLogic.toUpperCase()} —`);
    }
    lines.push(describeGroup(group, fields, operatorsByType));
  });
  return lines;
}
